import React from "react";

export interface BarChartDataItem {
  label: string;
  value: number;
  color?: string; // Optional color for individual bars
}

interface SimpleBarChartProps {
  data: BarChartDataItem[];
  width?: number; // Overall width of the chart
  height?: number; // Overall height of the chart (max bar height area)
  barColor?: string; // Default color for bars if not specified in data item
  barSpacing?: number; // Space between bars in pixels
  showValues?: boolean; // Whether to display the value on top of the bar
  valueColor?: string; // Color for the value text
  labelColor?: string; // Color for the label text
}

const SimpleBarChart: React.FC<SimpleBarChartProps> = ({
  data,
  height = 250,
  barColor = "#4A90E2", // A nice blue
  barSpacing = 2,
  showValues = false,
  valueColor = "#333",
  labelColor = "#555",
}) => {
  if (!data || data.length === 0) {
    return <div style={{ width: "100%", height, textAlign: "center", paddingTop: "20px" }}>No data to display.</div>;
  }

  const maxValue = Math.max(...data.map((item) => item.value), 0);

  // If maxValue is 0, all bars will have 0 height. Avoid division by zero.
  const scaleFactor = maxValue > 0 ? height / maxValue : 0;

  //const numBars = data.length;
  // const totalSpacing = (numBars - 1) * barSpacing;
  //const barWidth = (width - totalSpacing) / numBars;
  const barWidth = 5;

  // Ensure barWidth is not negative if spacing is too large for the width
  const effectiveBarWidth = Math.max(0, barWidth);

  const valueLabelHeight = 20; // Approximate height for value text
  const textPadding = 5; // Padding above value text

  return (
    <div
      style={{
        width: "100%",
        // Total height including space for labels below bars
        // Add some arbitrary space for labels if not showing values above
        height: `${height + (showValues ? valueLabelHeight + textPadding : 0) + 30}px`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: "1px solid #ccc",
        padding: "10px",
        boxSizing: "border-box",
        fontFamily: "Arial, sans-serif",
      }}
      role="figure"
      aria-label="Bar chart"
    >
      <div
        style={{
          width: "100%",
          height: `${height + (showValues ? valueLabelHeight + textPadding : 0)}px`,
          display: "flex",
          alignItems: "flex-end", // Bars grow from the bottom
          justifyContent: "flex-start", // Start bars from the left
          gap: `${barSpacing}px`, // Using gap for spacing
          position: "relative", // For positioning values absolutely if needed
        }}
      >
        {data.map((item, index) => {
          const barHeight = item.value * scaleFactor;
          const currentBarColor = item.color || barColor;

          return (
            <div
              key={item.label || index}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                height: "100%", // Take full height of the bar area + value space
                width: `${effectiveBarWidth}px`,
              }}
              role="presentation" // Each bar group is part of the larger figure
            >
              {showValues && (
                <div
                  style={{
                    height: `${valueLabelHeight}px`,
                    color: valueColor,
                    fontSize: "12px",
                    textAlign: "center",
                    marginBottom: `${textPadding}px`, // Space between value and top of bar
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  aria-label={`Value for ${item.label}: ${item.value}`}
                >
                  {item.value}
                </div>
              )}
              <div
                style={{
                  width: 5,
                  height: `${barHeight}px`,
                  backgroundColor: currentBarColor,
                  transition: "height 0.3s ease-out", // Optional: smooth transition
                }}
                title={`${item.label}: ${item.value}`} // Tooltip
                aria-label={`Bar for ${item.label}, value ${item.value}`}
              />
            </div>
          );
        })}
      </div>
      {/* Labels Container */}
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-start",
          marginTop: "5px",
          gap: `${barSpacing}px`, // Align labels with bars
        }}
      >
        {data.map((item, index) => (
          <div
            key={`label-${item.label || index}`}
            style={{
              width: `${effectiveBarWidth}px`,
              textAlign: "center",
              fontSize: "12px",
              color: labelColor,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            aria-hidden="true" // Labels are descriptive, data conveyed by bar + value text
          >
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SimpleBarChart;
